// Stores the visitor's cookie choice in a first-party cookie and tells
// subscribers when it changes. "Necessary" is always on; Google Maps
// ("externalMedia") is opt-in.

export const CONSENT_COOKIE = "swt_consent";
// Raise this when the list of services changes: every visitor is asked again.
export const CONSENT_VERSION = 1;
const MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

export type Consent = {
  version: number;
  date: string;
  externalMedia: boolean;
};

type Listener = () => void;

const consentListeners = new Set<Listener>();
const settingsListeners = new Set<Listener>();

const readCookie = (name: string): string | null => {
  if (typeof document === "undefined") return null;
  const match = document.cookie
    .split("; ")
    .find((part) => part.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
};

export const readConsent = (): Consent | null => {
  try {
    const raw = readCookie(CONSENT_COOKIE);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      parsed?.version !== CONSENT_VERSION ||
      typeof parsed?.externalMedia !== "boolean"
    ) {
      return null;
    }
    return parsed as Consent;
  } catch {
    return null;
  }
};

export const saveConsent = (choice: { externalMedia: boolean }): Consent => {
  const previous = readConsent();
  const consent: Consent = {
    version: CONSENT_VERSION,
    date: new Date().toISOString(),
    externalMedia: choice.externalMedia,
  };
  const secure =
    typeof window !== "undefined" && window.location.protocol === "https:"
      ? "; Secure"
      : "";
  document.cookie = `${CONSENT_COOKIE}=${encodeURIComponent(
    JSON.stringify(consent)
  )}; Max-Age=${MAX_AGE_SECONDS}; Path=/; SameSite=Lax${secure}`;
  consentListeners.forEach((listener) => listener());

  // Google Maps cannot be unloaded from a running page. When a visitor
  // withdraws consent after Maps was loaded, reload so it is gone.
  if (
    previous?.externalMedia &&
    !consent.externalMedia &&
    typeof window !== "undefined" &&
    (window as any).google?.maps
  ) {
    window.location.reload();
  }
  return consent;
};

export const subscribeConsent = (listener: Listener): (() => void) => {
  consentListeners.add(listener);
  return () => {
    consentListeners.delete(listener);
  };
};

// "Cookie settings" link in the footer re-opens the banner.
export const openConsentSettings = (): void => {
  settingsListeners.forEach((listener) => listener());
};

export const subscribeSettingsRequest = (listener: Listener): (() => void) => {
  settingsListeners.add(listener);
  return () => {
    settingsListeners.delete(listener);
  };
};
